/**
 * User Settings Modal
 * 
 * Allows users to:
 * - View/edit display name
 * - Upload/remove profile photo
 * - Set/change password (adaptive for magic link vs password users)
 * - Sign out
 */

import React, { useState, useRef } from "react";
import { useAuth } from "../../context/AuthContext";

// ============================================================
// THEME
// ============================================================
const THEME = {
  bg: "#0a0a0f",
  bgElevated: "#12121a",
  bgCard: "#16161f",
  bgHover: "#1e1e2a",
  accent: "#2fd6b5",
  accentHover: "#26b89a",
  accentGlow: "rgba(47, 214, 181, 0.15)",
  lime: "#cdde25",
  danger: "#ef4444",
  dangerGlow: "rgba(239, 68, 68, 0.15)",
  textPrimary: "#ffffff",
  textSecondary: "#a1a1aa",
  textMuted: "#71717a",
  border: "rgba(255, 255, 255, 0.08)",
  borderHover: "rgba(255, 255, 255, 0.15)",
  gradientAccent: "linear-gradient(135deg, #2fd6b5 0%, #cdde25 100%)",
};

// ============================================================
// PASSWORD SUCCESS MODAL COMPONENT
// ============================================================
function PasswordSuccessModal({ onClose }) {
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10001,
      padding: 24,
      backdropFilter: "blur(8px)",
    }}>
      <div style={{
        background: THEME.bgElevated,
        borderRadius: 20,
        padding: "40px 48px",
        textAlign: "center",
        border: `1px solid ${THEME.border}`,
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
        maxWidth: 400,
      }}>
        {/* Success Icon */}
        <div style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: THEME.accentGlow,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
          fontSize: 36,
        }}>
          ✅
        </div>
        
        {/* Title */}
        <h2 style={{
          fontSize: 22,
          fontWeight: 600,
          color: THEME.textPrimary,
          marginBottom: 12,
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          Password Changed Successfully
        </h2>
        
        {/* Message */}
        <p style={{
          fontSize: 14,
          color: THEME.textSecondary,
          lineHeight: 1.6,
          marginBottom: 28,
        }}>
          Your password has been updated. You can now use your new password to sign in.
        </p>
        
        {/* OK Button */}
        <button
          onClick={onClose}
          style={{
            background: THEME.gradientAccent,
            border: "none",
            borderRadius: 10,
            padding: "14px 48px",
            fontSize: 15,
            fontWeight: 600,
            color: "#111",
            cursor: "pointer",
            fontFamily: "'Space Grotesk', sans-serif",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.filter = "brightness(1.1)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = "brightness(1)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 24,
    backdropFilter: "blur(4px)",
  },
  modal: {
    background: THEME.bgElevated,
    borderRadius: 20,
    width: "100%",
    maxWidth: 440,
    maxHeight: "90vh",
    overflow: "auto",
    border: `1px solid ${THEME.border}`,
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
  },
  header: {
    padding: "24px 28px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: THEME.textPrimary,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  closeButton: {
    background: "none",
    border: "none",
    color: THEME.textMuted,
    fontSize: 24,
    cursor: "pointer",
    padding: 4,
    lineHeight: 1,
    borderRadius: 8,
    transition: "color 0.2s, background 0.2s",
  },
  content: {
    padding: "24px 28px 28px",
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: THEME.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: "'Space Grotesk', sans-serif",
  },
  avatarSection: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 16,
    background: THEME.gradientAccent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    fontWeight: 700,
    color: THEME.bg,
    overflow: "hidden",
    flexShrink: 0,
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    border: `3px solid ${THEME.border}`,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarActions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  avatarHint: {
    fontSize: 12,
    color: THEME.textMuted,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: THEME.textSecondary,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  input: {
    padding: "12px 14px",
    fontSize: 14,
    background: THEME.bgCard,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    color: THEME.textPrimary,
    fontFamily: "'Space Grotesk', sans-serif",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  inputFocus: {
    borderColor: THEME.accent,
    boxShadow: `0 0 0 3px ${THEME.accentGlow}`,
  },
  inputReadonly: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
  button: {
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 600,
    background: THEME.bgCard,
    color: THEME.textSecondary,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Grotesk', sans-serif",
    transition: "all 0.2s ease",
  },
  buttonPrimary: {
    background: THEME.accent,
    color: THEME.bg,
    border: `1px solid ${THEME.accent}`,
  },
  buttonDanger: {
    background: THEME.dangerGlow,
    color: THEME.danger,
    border: `1px solid rgba(239, 68, 68, 0.3)`,
  },
  buttonSmall: {
    padding: "8px 12px",
    fontSize: 12,
  },
  buttonRow: {
    display: "flex",
    gap: 10,
  },
  divider: {
    height: 1,
    background: THEME.border,
    margin: "8px 0",
  },
  error: {
    padding: "10px 14px",
    background: THEME.dangerGlow,
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 8,
    color: THEME.danger,
    fontSize: 13,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  success: {
    padding: "10px 14px",
    background: THEME.accentGlow,
    border: `1px solid rgba(47, 214, 181, 0.3)`,
    borderRadius: 8,
    color: THEME.accent,
    fontSize: 13,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  passwordSection: {
    background: THEME.bgCard,
    borderRadius: 12,
    padding: 16,
    border: `1px solid ${THEME.border}`,
  },
  passwordToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  },
  passwordToggleText: {
    fontSize: 14,
    color: THEME.textPrimary,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  passwordToggleHint: {
    fontSize: 12,
    color: THEME.textMuted,
    marginTop: 4,
  },
  chevron: {
    color: THEME.textMuted,
    transition: "transform 0.2s",
  },
  chevronExpanded: {
    transform: "rotate(180deg)",
  },
  passwordForm: {
    marginTop: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    paddingTop: 16,
    borderTop: `1px solid ${THEME.border}`,
  },
  signOutSection: {
    paddingTop: 8,
  },
  hiddenInput: {
    display: "none",
  },
};

// ============================================================
// USER SETTINGS MODAL
// ============================================================
export default function UserSettingsModal({ isOpen, onClose }) {
  const {
    userName,
    userAvatar,
    userEmail,
    hasPassword,
    updateUserName,
    updatePassword,
    uploadAvatar,
    removeAvatar,
    signOut,
  } = useAuth();

  // Form state
  const [displayName, setDisplayName] = useState(userName || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // UI state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Focus state
  const [nameFocused, setNameFocused] = useState(false);
  const [pass1Focused, setPass1Focused] = useState(false);
  const [pass2Focused, setPass2Focused] = useState(false);

  // File input ref
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle name save
  const handleSaveName = async () => {
    if (!displayName.trim()) {
      setError("Please enter a display name");
      return;
    }

    if (displayName === userName) {
      return; // No change
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateUserName(displayName.trim());
      setSuccess("Name updated successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to update name");
    } finally {
      setSaving(false);
    }
  };

  // Handle password save
  const handleSavePassword = async () => {
    setError("");
    setSuccess("");

    if (!newPassword) {
      setError("Please enter a password");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSaving(true);

    try {
      await updatePassword(newPassword);
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
      // Show the success modal
      setShowPasswordSuccessModal(true);
    } catch (err) {
      setError(err.message || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  // Handle password success modal close - redirect to home
  const handlePasswordSuccessClose = () => {
    setShowPasswordSuccessModal(false);
    onClose(); // Close the settings modal
    window.location.href = "/"; // Redirect to welcome page
  };

  // Handle avatar upload
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError("Image must be less than 2MB");
      return;
    }

    setUploadingAvatar(true);
    setError("");

    try {
      await uploadAvatar(file);
      setSuccess("Profile photo updated!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to upload photo");
    } finally {
      setUploadingAvatar(false);
      // Reset input
      e.target.value = "";
    }
  };

  const handleRemoveAvatar = async () => {
    setUploadingAvatar(true);
    setError("");

    try {
      await removeAvatar();
      setSuccess("Profile photo removed");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to remove photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to sign out");
    }
  };

  // Handle click outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Password Success Modal */}
      {showPasswordSuccessModal && (
        <PasswordSuccessModal onClose={handlePasswordSuccessClose} />
      )}
      
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal}>
          {/* Header */}
          <div style={styles.header}>
            <h2 style={styles.title}>User Settings</h2>
            <button
              style={styles.closeButton}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = THEME.textPrimary;
                e.currentTarget.style.background = THEME.bgHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = THEME.textMuted;
                e.currentTarget.style.background = "none";
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div style={styles.content}>
            {/* Messages */}
            {error && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.success}>{success}</div>}

          {/* Profile Photo */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Profile Photo</div>
            <div style={styles.avatarSection}>
              <div
                style={{
                  ...styles.avatar,
                  opacity: uploadingAvatar ? 0.6 : 1,
                }}
                onClick={handleAvatarClick}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow = `0 0 20px ${THEME.accentGlow}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {userAvatar ? (
                  <img src={userAvatar} alt="Avatar" style={styles.avatarImage} />
                ) : (
                  getInitials(userName || userEmail)
                )}
              </div>
              <div style={styles.avatarActions}>
                <div style={styles.buttonRow}>
                  <button
                    style={{ ...styles.button, ...styles.buttonSmall }}
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? "Uploading..." : "Upload Photo"}
                  </button>
                  {userAvatar && (
                    <button
                      style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                      onClick={handleRemoveAvatar}
                      disabled={uploadingAvatar}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={styles.avatarHint}>JPG, PNG or GIF. Max 2MB.</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={styles.hiddenInput}
              />
            </div>
          </div>

          <div style={styles.divider} />

          {/* Display Name */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Profile</div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Display Name</label>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onFocus={() => setNameFocused(true)}
                  onBlur={() => setNameFocused(false)}
                  placeholder="Your name"
                  style={{
                    ...styles.input,
                    ...(nameFocused ? styles.inputFocus : {}),
                    flex: 1,
                  }}
                />
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonPrimary,
                    opacity: saving || displayName === userName ? 0.6 : 1,
                  }}
                  onClick={handleSaveName}
                  disabled={saving || displayName === userName}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={userEmail || ""}
                readOnly
                style={{
                  ...styles.input,
                  ...styles.inputReadonly,
                }}
              />
            </div>
          </div>

          <div style={styles.divider} />

          {/* Password Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Security</div>
            <div style={styles.passwordSection}>
              <div
                style={styles.passwordToggle}
                onClick={() => setShowPasswordForm(!showPasswordForm)}
              >
                <div>
                  <div style={styles.passwordToggleText}>
                    {hasPassword ? "🔑 Change Password" : "🔑 Set Password"}
                  </div>
                  <div style={styles.passwordToggleHint}>
                    {hasPassword
                      ? "Update your account password"
                      : "Add a password to use password login alongside magic links"
                    }
                  </div>
                </div>
                <span
                  style={{
                    ...styles.chevron,
                    ...(showPasswordForm ? styles.chevronExpanded : {}),
                  }}
                >
                  ▼
                </span>
              </div>

              {showPasswordForm && (
                <div style={styles.passwordForm}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      {hasPassword ? "New Password" : "Password"}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onFocus={() => setPass1Focused(true)}
                      onBlur={() => setPass1Focused(false)}
                      placeholder="At least 6 characters"
                      style={{
                        ...styles.input,
                        ...(pass1Focused ? styles.inputFocus : {}),
                      }}
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onFocus={() => setPass2Focused(true)}
                      onBlur={() => setPass2Focused(false)}
                      placeholder="Confirm password"
                      style={{
                        ...styles.input,
                        ...(pass2Focused ? styles.inputFocus : {}),
                      }}
                    />
                  </div>
                  <button
                    style={{
                      ...styles.button,
                      ...styles.buttonPrimary,
                      width: "100%",
                      opacity: saving ? 0.6 : 1,
                    }}
                    onClick={handleSavePassword}
                    disabled={saving}
                  >
                    {saving
                      ? "Saving..."
                      : hasPassword
                        ? "Update Password"
                        : "Set Password"
                    }
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={styles.divider} />

          {/* Sign Out */}
          <div style={styles.signOutSection}>
            <button
              style={{
                ...styles.button,
                ...styles.buttonDanger,
                width: "100%",
              }}
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
